// API Configuration
// Centralized configuration for all API endpoints and settings
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL,
  BASE_URL_V1: process.env.REACT_APP_API_BASE_URL_V1 || process.env.REACT_APP_API_BASE_URL_v1, // Support both uppercase and lowercase
  WS_URL: process.env.REACT_APP_WS_URL,
  TIMEOUT: parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000, // 30 seconds
  // Main API endpoints
  ENDPOINTS: {
    AUTH: process.env.REACT_APP_AUTH_ENDPOINT || "/auth",
    HOSPITAL: process.env.REACT_APP_HOSPITAL_ENDPOINT || "/hospital",
    FLEET: process.env.REACT_APP_FLEET_ENDPOINT || "/fleet",
    BOOKING: process.env.REACT_APP_BOOKING_ENDPOINT || "/booking",
  },
  // Auth-specific endpoints (relative to AUTH_ENDPOINT)
  AUTH_ENDPOINTS: {
    REGISTER: "/register",
    REGISTER_WITH_OTP: "/register-with-otp",
    LOGIN: "/login",
    SEND_LOGIN_OTP: "/send-login-otp",
    COMPLETE_LOGIN: "/complete-login",
    LOGOUT: "/logout",
    PROFILE: "/profile",
    UPDATE_PROFILE: "/profile",
    CHANGE_PASSWORD: "/change-password",
    CHANGE_PASSWORD_REQUEST_OTP: "/change-password/request-otp",
    REFRESH: "/refresh",
    SEND_VERIFICATION: "/send-verification",
    VERIFY_CONTACT: "/verify-contact",
    FORGOT_PASSWORD: "/forgot-password",
    RESET_PASSWORD: "/reset-password",
    FIRST_TIME_PASSWORD_RESET: "/reset-password-first-login",
    // Super Admin specific endpoints
    REGISTER_FLEET_OPERATOR: "/admin/register-fleet-operator",
    GET_FLEET_OPERATORS: "/admin/fleet-operators",
    GET_FLEET_OPERATOR: "/admin/fleet-operators",
    UPDATE_FLEET_OPERATOR: "/admin/fleet-operators",
    DELETE_FLEET_OPERATOR: "/admin/fleet-operators",
    GET_ALL_USERS: "/admin/users",
    GET_USER: "/admin/users",
    UPDATE_USER: "/admin/users",
    DELETE_USER: "/admin/users",
  },
  // OTP endpoints
  OTP_ENDPOINTS: {
    GENERATE: "/otp/generate",
    VERIFY: "/otp/verify",
    CHECK: "/otp/check",
    RESEND: "/otp/resend",
  },
  // Enhanced auth endpoints
  ENHANCED_AUTH_ENDPOINTS: {
    REGISTER_WITH_OTP: "/register-with-otp",
    COMPLETE_REGISTRATION: "/complete-registration",
    LOGIN_WITH_OTP: "/login-with-otp",
    COMPLETE_OTP_LOGIN: "/complete-otp-login",
    SEND_VERIFICATION: "/send-verification",
    VERIFY_CONTACT: "/verify-contact",
  },
  // Hospital-specific endpoints
  HOSPITAL_ENDPOINTS: {
    LOGIN: "/v1/hospital/auth/login",
    SEND_LOGIN_OTP: "/v1/hospital/auth/send-login-otp",
    COMPLETE_LOGIN: "/v1/hospital/auth/complete-login",
    LOGOUT: "/v1/hospital/auth/logout",
    PROFILE: "/v1/hospital/auth/profile",
    UPDATE_PROFILE: "/v1/hospital/auth/update-profile",
    MY_PROFILE: "/v1/hospital/my-profile",
    FORGOT_PASSWORD: "/v1/hospital/auth/forgot-password",
    CHANGE_PASSWORD: "/v1/hospital/auth/change-password",
    REFRESH_TOKEN: "/v1/hospital/auth/refresh-token",
    CREATE_BOOKING: "/v1/hospital/bookings",
    GET_ACTIVE_BOOKINGS: "/v1/hospital/bookings/active",
    GET_BOOKING_HISTORY: "/v1/hospital/bookings/booking-history",
    GET_BOOKING_DETAILS: "/v1/hospital/bookings", // Updated: /v1/hospital/bookings/:bookingId
    GET_BOOKING_TRACKING: "/v1/hospital/bookings", // Updated: /v1/hospital/bookings/:bookingId/tracking
    UPDATE_BOOKING_STATUS: "/v1/hospital/bookings", // Updated: /v1/hospital/bookings/:bookingId/status
    CANCEL_BOOKING: "/v1/hospital/bookings", // Updated: /v1/hospital/bookings/:bookingId/cancel
    GET_BOOKING_STATUS_HISTORY: "/v1/hospital/bookings", // Updated: /v1/hospital/bookings/:bookingId/status-history
    GET_BOOKING_TRACKING_EVENTS: "/v1/hospital/bookings", // Updated: /v1/hospital/bookings/:bookingId/tracking/events
    GET_VEHICLE_HISTORY: "/v1/hospital/vehicles", // GET /v1/hospital/vehicles/:vehicle_id/history
    INITIATE_BOOKING_PAYMENT: "/v1/hospital/bookings", // POST /v1/hospital/bookings/:bookingId/initiate-payment
    SMART_MATCH: "/v1/hospital/bookings/smart-match",
    ESTIMATE_FARE: "/v1/hospital/bookings/estimate-fare",
    ESTIMATE_FARE_V2: "/v1/hospital/bookings/estimate-fare-v2",
    GET_CANDIDATES: "/v1/hospital/bookings/candidates",
    START_MANUAL_ALLOCATION: "/v1/hospital/bookings/manual-allocation/start",
    START_AUTO_ALLOCATION: "/v1/hospital/bookings/auto-allocation/start",
    TRIGGER_NEXT_DRIVER: "/v1/hospital/bookings/auto-allocation", // Updated: /v1/hospital/bookings/auto-allocation/:booking_id/next
    GET_ALLOCATION_STATUS: "/v1/hospital/bookings", // Updated: /v1/hospital/bookings/:bookingId/allocation-status
    STOP_ALLOCATION: "/v1/hospital/bookings", // Updated: /v1/hospital/bookings/:bookingId/stop-allocation
    SEARCH_PARTNERSHIPS: "/v1/hospital/partnerships/search",
    UPDATE_PARTNERSHIP: "/v1/hospital/partnerships", // Updated: /v1/hospital/partnerships/:partnershipId
    GET_PARTNERSHIP_PERFORMANCE: "/v1/hospital/partnerships", // Updated: /v1/hospital/partnerships/:partnershipId/performance
    // Partnership requests initiated by/for this hospital
    // GET /v1/hospital/partnerships/requests
    GET_PARTNERSHIP_REQUESTS: "/v1/hospital/partnerships/requests",
    UPDATE_PARTNERSHIP_REQUEST: "/v1/hospital/partnerships/requests", // Updated: /v1/hospital/partnerships/requests/:requestId
    DELETE_PARTNERSHIP_REQUEST: "/v1/hospital/partnerships/requests", // Updated: /v1/hospital/partnerships/requests/:requestId
    RESPOND_TO_PARTNERSHIP_INVITATION: "/v1/hospital/partnerships/invitations", // Updated: /v1/hospital/partnerships/invitations/:requestId/respond
    GET_PARTNER_FLEET_VEHICLES: "/v1/hospital/partner-fleet-vehicle",
    REGISTER: "/register",
    GET_PROFILE: "/profile",
    GET_ALL: "/all",
    DELETE: "/",
  },
  // Fleet-specific endpoints
  FLEET_ENDPOINTS: {
    // Auth endpoints (mounted at /v1/fleet/auth)
    REGISTER: "/v1/fleet/auth/register",
    LOGIN: "/v1/fleet/auth/login",
    SEND_LOGIN_OTP: "/v1/fleet/auth/send-login-otp",
    COMPLETE_LOGIN: "/v1/fleet/auth/complete-login",
    LOGOUT: "/v1/fleet/auth/logout",
    REFRESH_TOKEN: "/v1/fleet/auth/refresh-token",
    FORGOT_PASSWORD: "/v1/fleet/auth/forgot-password",
    VERIFY_RESET_OTP: "/v1/fleet/auth/verify-reset-otp",
    RESET_PASSWORD: "/v1/fleet/auth/reset-password",
    CHANGE_PASSWORD: "/v1/fleet/auth/change-password",
    // Profile endpoints (mounted at /v1/fleet/auth)
    GET_PROFILE: "/v1/fleet/auth/profile",
    UPDATE_PROFILE: "/v1/fleet/auth/update-profile",
    MY_PROFILE: "/v1/fleet/my-profile",
    // Business endpoints (mounted at /v1/fleet)
    GET_HOSPITALS: "/v1/fleet", // Will be combined with /{fleetId}/hospitals
    FETCH_HOSPITALS: "/v1/fleet/fetch-hospitals", // Get all hospitals for partnership selection
    // Partnership endpoints
    GET_PARTNERSHIPS: "/v1/fleet", // Will be combined with /{fleetId}/partnerships
    INVITE_HOSPITAL: "/v1/fleet", // Will be combined with /{fleetId}/invite-hospital
    GET_PARTNERSHIP_REQUESTS: "/v1/fleet", // Will be combined with /{fleetId}/partnership-requests
    UPDATE_PARTNERSHIP_REQUEST: "/v1/fleet", // Will be combined with /{fleetId}/partnership-requests/{requestId}
    DELETE_PARTNERSHIP_REQUEST: "/v1/fleet", // Will be combined with /{fleetId}/partnership-requests/{requestId}
    GET_INVITATIONS: "/v1/fleet", // Will be combined with /{fleetId}/invitations
    RESPOND_TO_INVITATION: "/v1/fleet", // Will be combined with /{fleetId}/invitations/{requestId}/respond
    // Assignment endpoints
    GET_ASSIGNMENTS: "/v1/fleet/assignments", // Direct endpoint
    GET_ASSIGNMENT_RESPONSES: "/v1/fleet/assignments", // Will be combined with /{bookingId}/responses
    GET_AVAILABLE_DRIVERS: "/v1/fleet/drivers/available", // Will be combined with /{ambulanceTypeId}
    ASSIGN_DRIVER: "/v1/fleet/assignments", // Will be combined with /{requestId}/assign
    REJECT_ASSIGNMENT: "/v1/fleet/assignments", // Will be combined with /{requestId}/reject
    // Booking endpoints
    CREATE_BOOKING: "/v1/fleet/bookings", // POST /v1/fleet/bookings
    GET_BOOKINGS: "/v1/fleet/bookings", // GET /v1/fleet/bookings
    GET_BOOKING_DETAILS: "/v1/fleet/bookings", // GET /v1/fleet/bookings/{bookingId}
    GET_BOOKING_HISTORY: "/v1/fleet/bookings/history", // GET /v1/fleet/bookings/history
    GET_VEHICLE_HISTORY: "/v1/fleet/vehicles", // GET /v1/fleet/vehicles/:vehicle_id/history
    UPDATE_BOOKING_STATUS: "/v1/fleet/bookings", // PUT /v1/fleet/bookings/{bookingId}/status
    CANCEL_BOOKING: "/v1/fleet/bookings", // PUT /v1/fleet/bookings/{bookingId}/cancel
    GET_BOOKING_STATUS_HISTORY: "/v1/fleet/bookings", // GET /v1/fleet/bookings/{bookingId}/status-history
    GET_BOOKING_TRACKING: "/v1/fleet/bookings", // GET /v1/fleet/bookings/{bookingId}/tracking
    ESTIMATE_BOOKING_FARE: "/v1/fleet/bookings/estimate-fare", // POST /v1/fleet/bookings/estimate-fare
    INITIATE_BOOKING_PAYMENT: "/v1/fleet/bookings", // POST /v1/fleet/bookings/{bookingId}/initiate-payment
    SMART_MATCH_BOOKING: "/v1/fleet/bookings/smart-match", // POST /v1/fleet/bookings/smart-match
    GET_CANDIDATES: "/v1/fleet/bookings/candidates", // POST /v1/fleet/bookings/candidates
    START_MANUAL_ALLOCATION: "/v1/fleet/bookings/manual-allocation/start", // POST /v1/fleet/bookings/manual-allocation/start
    START_AUTO_ALLOCATION: "/v1/fleet/bookings/auto-allocation/start", // POST /v1/fleet/bookings/auto-allocation/start
    TRIGGER_NEXT_DRIVER: "/v1/fleet/bookings/auto-allocation", // POST /v1/fleet/bookings/auto-allocation/{bookingId}/next
    GET_ALLOCATION_STATUS: "/v1/fleet/bookings", // GET /v1/fleet/bookings/{bookingId}/allocation-status
    STOP_ALLOCATION: "/v1/fleet/bookings", // POST /v1/fleet/bookings/{bookingId}/stop-allocation
    // Payment endpoints
    GET_PAYMENT_STATUS: "/v1/fleet/payments", // GET /v1/fleet/payments/{bookingId}/status
    GET_PAYMENT_ATTEMPTS: "/v1/fleet/payments", // GET /v1/fleet/payments/{bookingId}/attempts
    VERIFY_PAYMENT: "/v1/fleet/payments/verify", // POST /v1/fleet/payments/verify
  },
  // Ambulance endpoints
  AMBULANCE_ENDPOINTS: {
    TYPES: "/v1/fleet/ambulance-types",
  },
  // Command Center dispatch endpoints (mounted at /api/command-center-dispatch)
  COMMAND_CENTER_ENDPOINTS: {
    CREATE_BOOKING: "/command-center-dispatch/bookings",
    GET_BOOKINGS: "/command-center-dispatch/bookings",
    GET_CANDIDATES: "/command-center-dispatch/bookings", // combined with /{bookingId}/candidates
    SEARCH_CANDIDATES: "/command-center-dispatch/candidates/search",
    ASSIGN_DRIVER: "/command-center-dispatch/bookings", // combined with /{bookingId}/assign
    ASSIGN_MANUAL_DRIVER: "/command-center-dispatch/bookings", // combined with /{bookingId}/assign-manual
    REASSIGN_DRIVER: "/command-center-dispatch/bookings", // combined with /{bookingId}/reassign
    CANCEL_BOOKING: "/command-center-dispatch/bookings", // combined with /{bookingId}/cancel
    REQUEST_HOSPITAL: "/command-center-dispatch/hospitals/request",
    REQUIREMENT_OPTIONS: "/command-center-dispatch/requirement-options",
    SOURCE_OPTIONS: "/command-center-dispatch/source-options",
    PATIENT_CONDITION_OPTIONS: "/command-center-dispatch/patient-condition-options",
    PIPELINE_COUNTS: "/command-center-dispatch/pipeline-counts",
    // Deliberately NOT AMBULANCE_ENDPOINTS.TYPES ("/v1/fleet/ambulance-types")
    // — that one is TENANT_ADMIN-only. This is the unrestricted master list.
    AMBULANCE_TYPES: "/ambulances/types",
  },
  // Admin-managed Command Center booking form masters
  BOOKING_FORM_OPTIONS_ENDPOINTS: {
    REQUIREMENTS: "/booking-form-options/requirements",
    SOURCES: "/booking-form-options/sources",
    PATIENT_CONDITIONS: "/booking-form-options/patient-conditions",
    VEHICLE_TYPES: "/booking-form-options/vehicle-types",
  },
  // Pricing endpoints (Fare Estimate V2)
  PRICING_ENDPOINTS: {
    CALCULATE_FARE_V2: "/v1/pricing/calculate-fare-v2",
  },
  // Request headers
  HEADERS: {
    CONTENT_TYPE: "application/json",
    AUTHORIZATION: "Authorization",
    TENANT_ID: "x-tenant-id",
    CLIENT_TYPE: "x-client-type",
  },
  // Storage keys
  STORAGE_KEYS: {
    USER_DATA: "aps_user_data",
    JWT_TOKEN: "aps_jwt_token",
    REFRESH_TOKEN: "aps_refresh_token",
    HOSPITAL_ID: "aps_hospital_id",
    HOSPITAL_DATA: "aps_hospital_data",
    FLEET_ID: "aps_fleet_id",
    FLEET_DATA: "aps_fleet_data",
  },
  // Feature flags - Production-ready features enabled
  FEATURES: {
    ENABLE_OTP: true,
    ENABLE_SOCIAL_LOGIN: process.env.REACT_APP_ENABLE_SOCIAL_LOGIN === "true",
    DEBUG: process.env.REACT_APP_DEBUG === "true",
  },
};

// Helper function to get full endpoint URL
export const getEndpointUrl = (endpoint) => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

// Helper function to get auth endpoint URL
export const getAuthEndpointUrl = (endpoint) => {
  return `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH}${endpoint}`;
};

// Helper function to get OTP endpoint URL
export const getOtpEndpointUrl = (endpoint) => {
  return `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH}${endpoint}`;
};

// Helper function to get hospital endpoint URL
export const getHospitalEndpointUrl = (endpoint) => {
  return `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.HOSPITAL}${endpoint}`;
};

// Helper function to get fleet endpoint URL
export const getFleetEndpointUrl = (endpoint) => {
  return `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.FLEET}${endpoint}`;
};

// Helper function to get v1 endpoint URL (unified approach - no /api prefix)
export const getV1EndpointUrl = (endpoint) => {
  const baseUrl = API_CONFIG.BASE_URL_V1 || API_CONFIG.BASE_URL;

  // Remove trailing slash from base URL if present
  const cleanBaseUrl = baseUrl.replace(/\/$/, '');
  // Remove leading slash from endpoint if present (endpoint should start with /v1)
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const finalUrl = `${cleanBaseUrl}${cleanEndpoint}`;

  console.log('🔗 [getV1EndpointUrl] Unified routing (no /api):');
  console.log('  - endpoint:', endpoint);
  console.log('  - baseUrl:', baseUrl);
  console.log('  - finalUrl:', finalUrl);

  return finalUrl;
};
